import {rebootDevice} from "@/services/reboot.js";
import {systemInfo} from "@/systemInfo.js";
import {getCurrentState} from "@/services/state.js";
import {remoteAPI} from "@/services/axios.js";
import {REMOTE_WS_BASE_URL} from "@/constants/api.js";

const WS_HEARTBEAT_MS = 50000
const WS_PONG_TIMEOUT_MS = 10000
const WS_CONNECT_TIMEOUT_MS = 10000
const WS_STABLE_CONNECTION_MS = 10000
const WS_RECONNECT_BASE_MS = 2000
const WS_RECONNECT_MAX_MS = 30000

class DeviceManager {
    constructor(siteId, siteName, serialNumber, apiBaseUrl, wsBaseUrl) {
        this.siteId = String(siteId)
        this.serialNumber = serialNumber
        this.siteName = String(siteName)
        this.apiBaseUrl = apiBaseUrl
        this.wsBaseUrl = wsBaseUrl
        this.websocket = null
        this.heartbeatInterval = null
        this.pongTimeout = null
        this.stableTimeout = null
        this.awaitingPong = false
        this.serverSupportsPong = false
        this.lastSentStatus = null
        this.reconnectAttempts = 0
        this.isRegistered = false
        this.isReconnecting = false
        this.isDestroyed = false
        this.hasConnectedBefore = false

        // this.init()
    }

    async init() {
        try {
            await this.registerDevice()
            await this.connectWebSocket() // Connect WebSocket for real-time commands
            this.setupStateListener()
        } catch (error) {
            console.error('[REMOTE] Device manager initialization error:', error);

            // Check if it's an Axios error with a response
            if (error.response) {
                console.error('[REMOTE] Error response:', error.response.data);
                throw new Error(`Init failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
            }

            // Network errors or other issues
            throw new Error(`Remote Initialization failed: ${error.message}`);
        }
    }

    async registerDevice() {
        try {
            const authResponse = await remoteAPI.post('/devices/auth', {
                serial_number: this.serialNumber,
                site_id: this.siteId,
            })

            const data = authResponse.data
            const accessToken = data.access_token

            localStorage.setItem('remote_auth_token', accessToken)

            this.isRegistered = true;
        } catch (error) {
            console.error('[REMOTE] Device registration failed:', error)
            throw error
        }
    }

    async connectWebSocket() {
        if (this.isDestroyed) return

        this.teardownSocket()

        const wsUrl = `${REMOTE_WS_BASE_URL}/socket/device/${this.serialNumber}?site_id=${this.siteId}&token=${localStorage.getItem('remote_auth_token')}`

        return new Promise((resolve, reject) => {
            const socket = new WebSocket(wsUrl)
            this.websocket = socket

            const isCurrent = () => this.websocket === socket

            // Set up a timeout for connection attempts
            const timeout = setTimeout(() => {
                if (socket.readyState !== WebSocket.OPEN) {
                    socket.close()
                    reject(new Error('WebSocket connection timeout'))
                }
            }, WS_CONNECT_TIMEOUT_MS)

            socket.onopen = () => {
                if (!isCurrent()) return
                clearTimeout(timeout)
                this.isReconnecting = false

                // Start heartbeat to keep websocket connection alive
                clearInterval(this.heartbeatInterval)
                this.heartbeatInterval = setInterval(() => {
                    this.sendPing()
                }, WS_HEARTBEAT_MS) // set to stay under GCP 60s idle timeout

                // backoff only resets once the connection proves it can stay up
                clearTimeout(this.stableTimeout)
                this.stableTimeout = setTimeout(() => {
                    this.reconnectAttempts = 0
                }, WS_STABLE_CONNECTION_MS)

                systemInfo.remote = true
                this.sendStatus(getCurrentState())
                console.log('[REMOTE] WebSocket connected')
                resolve() // Connection successful
            }

            socket.onmessage = (event) => {
                if (!isCurrent()) return
                try {
                    const message = JSON.parse(event.data)

                    // Handle pong response from server
                    if (message.type === 'pong') {
                        this.handlePong()
                        return
                    }

                    // Handle ping from server — respond with pong
                    if (message.type === 'ping') {
                        this.sendPong()
                        return
                    }

                    this.handleCommand(message)
                } catch (error) {
                    console.error('[REMOTE] Failed to parse WebSocket message:', error)
                }
            }

            socket.onclose = () => {
                if (!isCurrent()) return
                clearTimeout(timeout)
                clearInterval(this.heartbeatInterval)
                clearTimeout(this.pongTimeout)
                clearTimeout(this.stableTimeout)
                this.awaitingPong = false
                this.lastSentStatus = null
                this.websocket = null
                systemInfo.remote = false
                console.log('[REMOTE] WebSocket disconnected')
                this.scheduleReconnect()
            }

            socket.onerror = (error) => {
                if (!isCurrent()) return
                clearTimeout(timeout)
                systemInfo.remote = false
                console.error('[REMOTE] WebSocket error:', error)
                reject(new Error('WebSocket connection failed'))
            }
        })
    }

    teardownSocket() {
        clearInterval(this.heartbeatInterval)
        clearTimeout(this.pongTimeout)
        clearTimeout(this.stableTimeout)
        this.awaitingPong = false
        this.lastSentStatus = null

        const socket = this.websocket
        if (!socket) return

        this.websocket = null
        socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) socket.close()
    }

    sendStatus(status) {
        if (this.websocket?.readyState !== WebSocket.OPEN) return
        if (status === this.lastSentStatus) return

        this.lastSentStatus = status
        this.websocket.send(JSON.stringify({
            type: 'device_status',
            status,
            timestamp: new Date().toISOString(),
            device_serial: this.serialNumber,
        }))
    }

    sendPing() {
        if (this.websocket?.readyState !== WebSocket.OPEN) return
        if (this.awaitingPong) return // still waiting for previous pong

        this.websocket.send(JSON.stringify({type: 'ping', status: getCurrentState()}))

        // stays disarmed until the server proves it answers app-level pings
        if (!this.serverSupportsPong) return

        this.awaitingPong = true
        this.pongTimeout = setTimeout(() => {
            console.warn('[REMOTE] Pong not received within timeout, closing connection')
            this.awaitingPong = false
            this.websocket?.close()
        }, WS_PONG_TIMEOUT_MS)
    }

    handlePong() {
        this.serverSupportsPong = true
        clearTimeout(this.pongTimeout)
        this.awaitingPong = false
    }

    sendPong() {
        if (this.websocket?.readyState !== WebSocket.OPEN) return
        this.websocket.send(JSON.stringify({
            type: 'pong',
            status: getCurrentState(),
            timestamp: new Date().toISOString(),
        }))
    }

    scheduleReconnect() {
        if (this.isDestroyed) return
        if (this.isReconnecting) return // Guard: don't stack multiple reconnect timers
        this.isReconnecting = true
        this.reconnectAttempts++

        // Exponential backoff: 2s → 4s → 8s → 16s → 30s (cap), ±25% jitter
        // Starts at 2s (not 1s) — gives the network a moment to recover
        const backoff = Math.min(WS_RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts - 1), WS_RECONNECT_MAX_MS)
        const delay = Math.round(backoff * (0.75 + Math.random() * 0.5))

        console.log(`[REMOTE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

        setTimeout(async () => {
            try {
                await this.connectWebSocket()
            } catch (error) {
                // connectWebSocket rejected (timeout or immediate error)
                // onclose will have fired already and scheduleReconnect called again
                // isReconnecting was reset in onopen (which didn't fire) — reset it here too
                this.isReconnecting = false
                console.error('[REMOTE] Reconnect attempt failed:', error.message)
            }
        }, delay)
    }

    async handleCommand(message) {
        console.log(`[REMOTE] Received message: ${JSON.stringify(message)}`)

        const {request_id, command, app, params} = message

        let success = false
        let error = null
        let status = null

        try {
            // Execute the command based on type
            if (command) {
                if (getCurrentState() === 'busy' && command !== 'reboot') {
                    console.log("[REMOTE] System busy. skipping command")
                    status = 'busy'
                    success = false
                    await this.sendCommandResponse(request_id, success, error, status)
                    return
                }

                switch (command) {
                    case 'welcome':
                        status = this.hasConnectedBefore ? getCurrentState() : 'loading'
                        success = true
                        this.hasConnectedBefore = true
                        await this.handleWelcome(message)
                        break

                    case 'reboot':
                        // status = 'rebooting'
                        success = await this.handleReboot()
                        break

                    case 'cancel_reboot':
                        success = await this.handleCancelReboot()
                        break

                    case 'launch_app':
                        status = app
                        success = await this.handleLaunchApp(app, params)
                        break

                    case 'return_home':
                        status = 'home'
                        success = await this.handleReturnHome()
                        break

                    case 'toggle_menu':
                        success = await this.handleToggleMenu()
                        break

                    default:
                        // console.warn(`[REMOTE] Unknown command: ${command}`)
                        throw new Error(`Unknown command: ${command}`)
                }
            }
        } catch (err) {
            error = err.message
            console.error('[REMOTE] Command execution failed:', err)
        }

        // Send response back
        await this.sendCommandResponse(request_id, success, error, status)
    }

    async handleWelcome(message) {
        const {request_id, command, app, params} = message;

        const onReturned = async ({detail}) => {
            window.removeEventListener('remote-welcome', onReturned);
            await this.sendCommandResponse(request_id, true, null, 'home');
        };

        window.addEventListener('remote-welcome', onReturned);
    }


    async handleReboot() {
        console.log('[REMOTE] Executing reboot');
        rebootDevice('remote');
        return true;
    }

    async handleCancelReboot() {
        console.log('[REMOTE] Cancelling reboot');
        window.dispatchEvent(new CustomEvent('remote-cancel-reboot'));
        // rebootDevice('', true);
        return true;
    }

    async handleReturnHome(switchingApps = false) {
        console.log('[REMOTE] Returning home');

        if (getCurrentState() === 'home') {
            console.log('[REMOTE] Already home. Skipping')
            window.dispatchEvent(new CustomEvent('remote-returned-home', {detail: {response: true}}));
            return true
        }

        window.dispatchEvent(new CustomEvent('remote-return-home', {detail: {switchingApps}}));

        // wait for the answer
        return new Promise(resolve => {
            function onReturned({detail}) {
                window.removeEventListener('remote-returned-home', onReturned); // tidy-up
                resolve(detail.response);               // true / false
            }

            window.addEventListener('remote-returned-home', onReturned);
        });
    }

    async handleToggleMenu() {
        console.log('[REMOTE] Toggling Menu');
        window.dispatchEvent(new CustomEvent('remote-toggle-menu'));
        return true;
    }

    async handleLaunchApp(appKey, params) {
        console.log('[REMOTE] Launching app:', appKey, params)

        if (getCurrentState() !== 'home') await this.handleReturnHome(true)

        await new Promise(resolve => setTimeout(resolve, 1000));
        window.dispatchEvent(new CustomEvent('remote-launch-app', {detail: {appKey, params}}))
        return true
    }

    async sendCommandResponse(requestId, success, error = null, status = null) {
        try {
            const response = {
                request_id: requestId,
                type: 'command_response',
                success,
                error,
                status,
                timestamp: new Date().toISOString(),
                device_serial: this.serialNumber,
            }

            // console.log(`[REMOTE] response: ${JSON.stringify(response)}`)

            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                this.websocket.send(JSON.stringify(response))
            } else {
                // Fallback to HTTP if WebSocket not available
                await fetch(`${this.apiBaseUrl}/device/command-response`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(response)
                })
            }

        } catch (error) {
            console.error('[REMOTE] Failed to send command response:', error)
        }
    }

    setupStateListener() {
        this.stateHandler = ({detail}) => {
            this.sendStatus(detail.state)
        };

        window.addEventListener('state-changed', this.stateHandler);
    }

    destroy() {
        this.isDestroyed = true
        this.teardownSocket()
        window.removeEventListener('state-changed', this.stateHandler);
    }
}

// Usage in your device Vue app:
// Initialize the device manager when your app starts
export async function initializeDeviceManager(siteId, siteName, serialNumber, apiBaseUrl, wsBaseUrl) {
    const deviceManager = new DeviceManager(siteId, siteName, serialNumber, apiBaseUrl, wsBaseUrl)
    await deviceManager.init()
    return deviceManager
}